import useSWR, { type SWRResponse } from "swr";
import { useApiClient } from "@/hooks/useApiClient";
import {
  corporateReceptionBooksApiPath,
  getCorporateReceptionBooks,
} from "../api";
import { inlineErrorSwrOptions } from "./inlineErrorSwrOptions";
import { DEFAULT_RECEPTION_BOOK_ORDERING } from "../utils/receptionBookOrdering";
import type {
  CorporateReceptionBooksParams,
  ICommercialFeedResponse,
  ReceptionBookOrdering,
} from "../types";

/** 受付帳履歴の1ページあたり件数（BE で受付日の新しい順にページ取得する） */
export const RECEPTION_BOOK_PAGE_SIZE = 10;

/**
 * 法人に紐付いた商業登記受付帳の履歴を1ページ分取得する（GET corporate-master/<法人番号>/reception-books）。
 * hasFullAccess（定額プラン）かつ13桁の法人番号のときのみ取得する
 * @param corporateNumber 法人番号（13桁）
 * @param hasFullAccess 受付帳を全件閲覧できるか
 * @param page 0始まりのページ番号
 * @param ordering 並び順（BE の ordering。既定は受付日の新しい順）
 * @returns SWRレスポンス（count は全件数、list は当該ページ分）
 */
export const useCorporateReceptionBooks = (
  corporateNumber: string,
  hasFullAccess = true,
  page = 0,
  ordering: ReceptionBookOrdering = DEFAULT_RECEPTION_BOOK_ORDERING
): SWRResponse<ICommercialFeedResponse, Error> => {
  const { apiClient } = useApiClient();
  const params: CorporateReceptionBooksParams = {
    fromCount: page * RECEPTION_BOOK_PAGE_SIZE,
    size: RECEPTION_BOOK_PAGE_SIZE,
    ordering,
  };
  return useSWR<ICommercialFeedResponse, Error>(
    hasFullAccess && /^\d{13}$/.test(corporateNumber)
      ? [corporateReceptionBooksApiPath(corporateNumber), params]
      : null,
    async () =>
      await getCorporateReceptionBooks(apiClient, corporateNumber, params),
    {
      revalidateOnFocus: false,
      keepPreviousData: true,
      ...inlineErrorSwrOptions,
    }
  );
};
